import axios from "axios";
import { SERVER_URL } from "../ServerLink";

const REFRESH_PATH = "auth/refresh";
const LOGOUT_PATH = "auth/logout";

const api = axios.create({
    baseURL: SERVER_URL + "/",
});

const REFRESH_KEY = "refreshToken";
const ACCESS_KEY = "accessToken";
const ACTIVITY_KEY = "lastActivityAt";
const ACCESS_TTL_KEY = "accessExpiresInSec";

function getAccessToken() { return window.localStorage.getItem(ACCESS_KEY) || ""; }
function getRefreshToken() { return window.localStorage.getItem(REFRESH_KEY) || ""; }
function setTokens({ accessToken, refreshToken }) {
    if (accessToken) window.localStorage.setItem(ACCESS_KEY, accessToken);
    if (refreshToken) window.localStorage.setItem(REFRESH_KEY, refreshToken);
}
function clearTokens() {
    window.localStorage.removeItem(ACCESS_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
    window.localStorage.removeItem("authUser");
    window.localStorage.removeItem("role");
    window.localStorage.removeItem(ACTIVITY_KEY);
    window.localStorage.removeItem(ACCESS_TTL_KEY);
}
function markActivity() {
    window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

let refreshInFlight = null;

async function performRefresh() {
    if (refreshInFlight) return refreshInFlight;
    const rt = getRefreshToken();
    if (!rt) {
        return Promise.reject(new Error("NO_REFRESH_TOKEN"));
    }
    refreshInFlight = axios
        .post(SERVER_URL + "/" + REFRESH_PATH, { refreshToken: rt })
        .then((res) => {
            const data = res && res.data;
            if (!data || !data.accessToken || !data.refreshToken) {
                throw new Error("BAD_REFRESH_RESPONSE");
            }
            setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
            if (data.accessExpiresIn) {
                window.localStorage.setItem(ACCESS_TTL_KEY, String(data.accessExpiresIn));
            }
            scheduleProactiveRefresh();
            return data.accessToken;
        })
        .finally(() => {
            refreshInFlight = null;
        });
    return refreshInFlight;
}

function forceLogoutRedirect() {
    clearTokens();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login?reason=session_expired";
    }
}

api.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
        config.headers.Authorization = "Bearer " + token;
    }
    markActivity();
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error && error.config;
        const status = error && error.response && error.response.status;
        const url = (original && original.url) || "";

        if (status !== 401 || !original || original.__isRetry) return Promise.reject(error);
        if (url.includes(REFRESH_PATH) || url.includes("/login") || url.includes("/auth/logout")) {
            forceLogoutRedirect();
            return Promise.reject(error);
        }

        try {
            const newAccess = await performRefresh();
            original.__isRetry = true;
            original.headers = original.headers || {};
            original.headers.Authorization = "Bearer " + newAccess;
            return api.request(original);
        } catch (refreshErr) {
            forceLogoutRedirect();
            return Promise.reject(refreshErr);
        }
    }
);

// Auto-logout after this much inactivity. Default: 1 hour (overridable at
// runtime via window.__RDC_IDLE_LIMIT_MS__).
const IDLE_LIMIT_MS = parseInt(window.__RDC_IDLE_LIMIT_MS__ || "0", 10) || 60 * 60 * 1000;
let idleTimer = null;

function resetIdleTimer() {
    markActivity();
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(async () => {
        try {
            const rt = getRefreshToken();
            if (rt) {
                await axios.post(SERVER_URL + "/" + LOGOUT_PATH, { refreshToken: rt }).catch(() => {});
            }
        } finally {
            forceLogoutRedirect();
        }
    }, IDLE_LIMIT_MS);
}

// Proactively renew the access token shortly before it expires so an active
// user is never interrupted by a 401 mid-request. Only renews while the user is
// still active (within the idle window); an idle user is left to the idle timer
// so the "log out after an hour of inactivity" rule still holds.
let proactiveTimer = null;
function scheduleProactiveRefresh() {
    if (proactiveTimer) clearTimeout(proactiveTimer);
    const ttlSec = parseInt(window.localStorage.getItem(ACCESS_TTL_KEY) || "0", 10) || 0;
    const ttlMs = ttlSec * 1000;
    // Renew at ~80% of the access-token lifetime (min 30s lead), or every 10 min
    // when the lifetime is unknown.
    const lead = ttlMs > 0
        ? Math.max(Math.floor(ttlMs * 0.8), ttlMs - 60 * 1000)
        : 10 * 60 * 1000;
    proactiveTimer = setTimeout(async () => {
        try {
            const last = parseInt(window.localStorage.getItem(ACTIVITY_KEY) || "0", 10);
            const idleFor = Date.now() - last;
            if (getRefreshToken() && idleFor < IDLE_LIMIT_MS) {
                await performRefresh();
                return; // performRefresh reschedules on success
            }
        } catch (_) {
            // A failed renewal surfaces on the next request's 401 → refresh path.
        }
        scheduleProactiveRefresh();
    }, lead);
}

if (typeof window !== "undefined") {
    const events = ["click", "keydown", "scroll", "mousemove", "touchstart", "visibilitychange"];
    events.forEach((ev) => window.addEventListener(ev, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    scheduleProactiveRefresh();

    window.addEventListener("storage", (e) => {
        if (e.key === ACCESS_KEY && !e.newValue) {
            forceLogoutRedirect();
        }
    });
}

export const auth = {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearTokens,
    markActivity,
    forceLogoutRedirect,
    performRefresh,
};

export default api;
